import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/LanguageContext";
import { Link } from "react-router-dom";

export const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { t, language } = useLanguage();

  useEffect(() => {
    const consent = localStorage.getItem("cookie-consent");
    if (!consent) {
      // Small delay so it doesn't pop up instantly on load
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptCookies = () => {
    localStorage.setItem("cookie-consent", "accepted");
    setIsVisible(false);
    // Here you could initialize Google Analytics or other tracking
  };

  const declineCookies = () => {
    localStorage.setItem("cookie-consent", "declined");
    setIsVisible(false);
  };

  const content = {
    en: {
      title: "We value your privacy",
      desc: "We use cookies to enhance your browsing experience, serve personalized ads or content, and analyze our traffic. By clicking \"Accept All\", you consent to our use of cookies.",
      accept: "Accept All",
      decline: "Decline",
      privacy: "Privacy Policy"
    },
    nl: {
      title: "Wij waarderen uw privacy",
      desc: "We gebruiken cookies om uw surfervaring te verbeteren, gepersonaliseerde advertenties of content te tonen en ons verkeer te analyseren. Door op \"Accepteer alles\" te klikken, stemt u in met ons gebruik van cookies.",
      accept: "Accepteer alles",
      decline: "Weiger",
      privacy: "Privacybeleid"
    }
  };

  const text = content[language as keyof typeof content] || content.nl;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 pointer-events-none"
        >
          <div className="container mx-auto max-w-5xl">
            <div className="bg-background/90 backdrop-blur-xl border border-border/50 shadow-2xl rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 pointer-events-auto">
              <div className="flex-1 max-w-3xl">
                <h3 className="text-lg font-semibold tracking-tight mb-2">{text.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {text.desc}{" "}
                  <Link to="/privacybeleid" className="text-foreground underline underline-offset-4 hover:opacity-80 transition-opacity">
                    {text.privacy}
                  </Link>
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
                <Button variant="outline" onClick={declineCookies} className="rounded-full px-6">
                  {text.decline}
                </Button>
                <Button onClick={acceptCookies} className="rounded-full px-6">
                  {text.accept}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
